/**
 * Souqli Help Centre - smart support engine.
 *
 * Mirrors the smart listing agent's philosophy: a single, deterministic
 * classifier with NO external API. The user types a plain-language question
 * ("where's my order", "how do I get paid", "item is fake") and `searchHelp`
 * ranks the knowledge base by keyword overlap - instant, offline and free.
 *
 * Modelled on how Amazon and Vinted organise support:
 *   - Amazon: task-oriented topic tiles (Your Orders, Returns & Refunds,
 *     Payments, Account, Security) with a prominent search and a contact
 *     fallback at the end.
 *   - Vinted: Buying / Selling / Trust & Safety, an AI-summarised search, a
 *     "popular topics" shelf, and per-order "I have an issue" help.
 *
 * To extend: add an entry to ARTICLES with good `keywords`, and (optionally)
 * surface it in POPULAR. That's it - the matcher and the page pick it up.
 */

export interface HelpTopic {
  id: string;
  title: string;
  icon: string;
  blurb: string;
}

export interface HelpArticle {
  id: string;
  topic: string; // HelpTopic id
  title: string;
  /** Plain-language terms a person might type. Keep generous - matching is token based. */
  keywords: string[];
  /** One-line answer shown in search results and topic lists. */
  summary: string;
  /** Full answer, as short paragraphs. */
  body: string[];
  /** Optional ordered steps. */
  steps?: string[];
  /** Optional primary call-to-action link into the app. */
  link?: { to: string; label: string };
}

/* ─────────────────────────── Topics (task tiles) ─────────────────────────── */

export const TOPICS: HelpTopic[] = [
  { id: 'buying',       title: 'Buying & orders',    icon: '🛍️', blurb: 'Track an order, delivery problems, offers.' },
  { id: 'returns',      title: 'Returns & refunds',  icon: '↩️', blurb: 'Send something back and get your money.' },
  { id: 'selling',      title: 'Selling & listings', icon: '🏷️', blurb: 'List an item, fees, postage, reviews.' },
  { id: 'payments',     title: 'Payments & payouts', icon: '💷', blurb: 'Payments, wallet, getting paid, fees.' },
  { id: 'delivery',     title: 'Delivery & tracking',icon: '📦', blurb: 'Where your parcel is and when it lands.' },
  { id: 'account',      title: 'Account & sign-in',  icon: '👤', blurb: 'Password, email & Google login.' },
  { id: 'verification', title: 'ID verification',    icon: '🪪', blurb: 'Verify your identity to sell and pay out.' },
  { id: 'trust',        title: 'Trust & safety',     icon: '🛡️', blurb: 'Buyer protection, disputes, staying safe.' },
  { id: 'membership',   title: 'Membership',         icon: '⭐', blurb: 'What Member gives you and how to manage it.' },
];

/* ─────────────────────────── Knowledge base ─────────────────────────── */

export const ARTICLES: HelpArticle[] = [
  /* ---- Buying & orders ---- */
  {
    id: 'track-order', topic: 'buying', title: 'Where is my order?',
    keywords: ['track', 'tracking', 'where', 'order', 'parcel', 'package', 'status', 'shipped', 'dispatched', 'delivery'],
    summary: 'Follow your parcel from dispatch to delivery in real time.',
    body: [
      'Every Souqli order is tracked end to end. As soon as the seller books postage you get a tracking code, and each scan updates your order status automatically.',
      'You can follow it from your orders page, or paste the tracking code into our parcel tracker.',
    ],
    steps: [
      'Open My Souqli → Orders.',
      'Select the order to see its live tracking timeline.',
      'Tap the tracking code to view the courier’s latest scan.',
    ],
    link: { to: '/dashboard/orders', label: 'View my orders' },
  },
  {
    id: 'order-not-arrived', topic: 'buying', title: 'My order hasn’t arrived',
    keywords: ['not arrived', 'missing', 'lost', 'late', 'never came', 'not delivered', 'didn’t arrive', 'didnt arrive', 'where'],
    summary: 'Your payment is held safely. Here’s what to do if a parcel is late or missing.',
    body: [
      'Because your payment is held securely by Stripe until you confirm the item arrived, your money is protected the whole time a parcel is in transit.',
      'First, check the tracking on your order - couriers sometimes mark a parcel "delivered" a little early, or leave it with a neighbour or in a locker. If the estimated date has passed with no movement, open a dispute from the order and our team will step in.',
    ],
    steps: [
      'Open the order and check the latest tracking scan.',
      'Message the seller a question from the order if it looks stuck.',
      'Still nothing? Tap "Get help with this order" to open a dispute before the protection window closes.',
    ],
    link: { to: '/dashboard/orders', label: 'Get help with an order' },
  },
  {
    id: 'item-not-as-described', topic: 'buying', title: 'My item is damaged, fake or not as described',
    keywords: ['not as described', 'wrong', 'damaged', 'broken', 'fake', 'counterfeit', 'different', 'not genuine', 'replica', 'condition'],
    summary: 'Open a dispute within the protection window and we’ll refund you if it’s upheld.',
    body: [
      'If what arrived doesn’t match the listing - damaged, faulty, the wrong item, or a suspected counterfeit - you’re covered by Souqli buyer protection. Your payment stays on hold until the issue is resolved.',
      'Open a dispute from the order within the window shown on it, add clear photos and a short description, and our team will mediate. Approved disputes are refunded to you.',
    ],
    steps: [
      'Open the order and choose "Get help with this order".',
      'Pick the reason and upload photos of the problem.',
      'Submit - funds stay held while we review, usually within a couple of business days.',
    ],
    link: { to: '/dashboard/orders', label: 'Open a dispute' },
  },
  {
    id: 'cancel-order', topic: 'buying', title: 'Cancel or change an order',
    keywords: ['cancel', 'change', 'mistake', 'wrong item', 'changed my mind', 'stop order'],
    summary: 'You can cancel before the seller ships. After dispatch, use a return instead.',
    body: [
      'If the seller hasn’t posted the item yet, message them from the order to ask for a cancellation - the payment simply is returned to you.',
      'Once it’s on its way, you can’t cancel, but you can start a return once it arrives if it qualifies.',
    ],
    link: { to: '/dashboard/orders', label: 'Go to my orders' },
  },
  {
    id: 'make-offer', topic: 'buying', title: 'Make an offer or place a bid',
    keywords: ['offer', 'bid', 'negotiate', 'haggle', 'price', 'lower price', 'best price', 'auction'],
    summary: 'Many listings accept offers, and some run as timed auctions.',
    body: [
      'Where a seller has enabled offers, you’ll see an "Offers open" badge and a Make an offer button on the listing. Send your price and the seller can accept, decline or counter.',
      'Auction listings run for a set time - place your maximum bid and we’ll bid up to it on your behalf.',
    ],
  },

  /* ---- Returns & refunds ---- */
  {
    id: 'how-returns-work', topic: 'returns', title: 'How returns work',
    keywords: ['return', 'send back', 'give back', 'return item', 'how to return', 'returns policy'],
    summary: 'Start a return from the order within the return window; funds stays on hold until it’s settled.',
    body: [
      'If an item qualifies for a return, you start it from the order within the return window shown there. You’ll get return instructions, and once the seller receives the item (or a dispute is upheld) your refund is issued.',
      'Items sold as described and accurately graded may not always be returnable for a simple change of mind - check the listing and the order for the return window.',
    ],
    link: { to: '/dashboard/orders', label: 'Start a return' },
  },
  {
    id: 'refund-timing', topic: 'returns', title: 'When will I get my refund?',
    keywords: ['refund', 'money back', 'how long refund', 'refund time', 'when refund', 'reimburse'],
    summary: 'Refunds are released to the seller once the return or dispute is resolved, then take a few days to reach your bank.',
    body: [
      'As soon as a return is confirmed or a dispute is upheld, we issue the refund. It goes back to your original payment method, or to your Souqli wallet if you prefer.',
      'Card refunds then take a few business days to appear, depending on your bank.',
    ],
  },
  {
    id: 'return-postage', topic: 'returns', title: 'Who pays for return postage?',
    keywords: ['return postage', 'return label', 'return cost', 'who pays return', 'return shipping'],
    summary: 'It depends on the reason - faulty or not-as-described returns are covered differently to change-of-mind.',
    body: [
      'If an item is faulty, damaged or not as described, you shouldn’t be out of pocket - follow the return steps on the order and keep your proof of postage.',
      'For other returns, the return instructions on the order will make clear who covers postage before you send anything back.',
    ],
  },

  /* ---- Selling & listings ---- */
  {
    id: 'how-to-list', topic: 'selling', title: 'How to list an item',
    keywords: ['sell', 'list', 'listing', 'upload', 'add item', 'create listing', 'post item', 'photos', 'how to sell'],
    summary: 'Add photos, let the smart listing tool draft the details, set your price, publish.',
    body: [
      'Listing takes a couple of minutes. Add clear photos and a short description and Souqli’s smart listing tool suggests the category, condition grade and title for you - you just review and tweak.',
      'Set your price, confirm postage, and publish. Listings are checked before they go live (see "Why is my listing pending?").',
    ],
    link: { to: '/dashboard/sell', label: 'Start a listing' },
  },
  {
    id: 'listing-pending', topic: 'selling', title: 'Why is my listing pending or under review?',
    keywords: ['pending', 'review', 'under review', 'not live', 'approved', 'waiting', 'listing rejected'],
    summary: 'Every listing is reviewed before going live to keep the marketplace safe and genuine.',
    body: [
      'To protect buyers, Souqli reviews new listings before they appear publicly - this checks for prohibited, counterfeit or stolen items. Most are approved quickly.',
      'If a listing needs a change, you’ll be told why so you can fix and resubmit it.',
    ],
    link: { to: '/dashboard/listed', label: 'See my listings' },
  },
  {
    id: 'seller-fees', topic: 'selling', title: 'Souqli fees - what does it cost to sell?',
    keywords: ['fee', 'fees', 'commission', 'charge', 'cost to sell', 'seller fee', 'how much', 'cut', 'take'],
    summary: 'Sellers keep the full item price. Souqli’s revenue comes from a buyer service fee.',
    body: [
      'You keep the full price you set for your item. Souqli’s revenue comes from a small service fee added at checkout for the buyer, not a commission taken from your sale.',
      'Members can have the buyer service fee waived - see the Membership topic.',
    ],
    link: { to: '/trust', label: 'How payments work' },
  },
  {
    id: 'get-paid', topic: 'selling', title: 'How and when do I get paid?',
    keywords: ['get paid', 'payout', 'payment', 'when paid', 'money', 'seller payment', 'release funds', 'wallet'],
    summary: 'The buyer’s payment is held until you confirm receipt and released to your wallet after delivery is confirmed.',
    body: [
      'When someone buys your item, their payment is held safely until you confirm receipt. Once the buyer confirms the item arrived as described (or the inspection window passes with no dispute), the funds are released to your Souqli wallet.',
      'From your wallet you can withdraw to your bank. Payouts require a verified identity - see the ID verification topic.',
    ],
    link: { to: '/dashboard/wallet', label: 'Open my wallet' },
  },
  {
    id: 'postage-label', topic: 'selling', title: 'Postage & shipping labels',
    keywords: ['ship', 'postage', 'label', 'send item', 'shipping label', 'send parcel', 'drop off', 'courier'],
    summary: 'Buy a tracked label in a couple of taps and send from your nearest drop-off point.',
    body: [
      'When an item sells, you can buy a tracked shipping label right from the order. Print it (or show the QR code at a drop-off point), attach it, and the tracking updates the buyer automatically.',
      'Always use the tracked label from the order so your sale is covered and the buyer can follow the parcel.',
    ],
    link: { to: '/dashboard/sales', label: 'Go to my sales' },
  },

  /* ---- Payments & payouts ---- */
  {
    id: 'how-escrow', topic: 'payments', title: 'How payments and buyer protection work',
    keywords: ['escrow', 'held', 'secure', 'safe payment', 'protection', 'held funds', 'how payment works'],
    summary: 'Your payment is held securely until the item arrives as described - protecting both sides.',
    body: [
      'Souqli holds every payment securely until you confirm receipt. The seller can see the sale is funded and post with confidence, and your money isn’t released to them until you’ve received the item as described.',
      'If something goes wrong, the funds stay put while we mediate - so no one is left out of pocket.',
    ],
    link: { to: '/trust', label: 'Learn about buyer protection' },
  },
  {
    id: 'payment-methods', topic: 'payments', title: 'Which payment methods can I use?',
    keywords: ['card', 'pay', 'visa', 'mastercard', 'apple pay', 'google pay', 'payment method', 'debit', 'credit'],
    summary: 'All major cards and mobile wallets, processed securely by Stripe.',
    body: [
      'Checkout is powered by Stripe, so you can pay with major debit and credit cards and supported mobile wallets. Souqli never sees or stores your full card number.',
    ],
  },
  {
    id: 'buyer-fee', topic: 'payments', title: 'What is the buyer service fee?',
    keywords: ['service fee', 'buyer fee', 'extra charge', 'checkout fee', 'why extra', 'protection fee'],
    summary: 'A small fee at checkout that funds buyer protection, buyer protection and support. Waived for Members.',
    body: [
      'The buyer service fee is a small amount added at checkout. It funds the things that make Souqli safe - buyer protection, verification and support.',
      'Souqli Members can have this fee waived. See the Membership topic for what’s included.',
    ],
  },
  {
    id: 'wallet-withdraw', topic: 'payments', title: 'Withdraw money from your wallet',
    keywords: ['withdraw', 'cash out', 'bank', 'take money out', 'transfer', 'payout to bank', 'wallet balance'],
    summary: 'Move your available balance to your bank once your identity is verified.',
    body: [
      'Money you’ve earned sits in your Souqli wallet. Once your identity is verified you can withdraw your available balance to your bank account.',
      'For your security, withdrawals are rate-limited and confirmed - if a withdrawal doesn’t appear, check it wasn’t still pending the payout release on a recent sale.',
    ],
    link: { to: '/dashboard/wallet', label: 'Open my wallet' },
  },

  /* ---- Delivery & tracking ---- */
  {
    id: 'track-parcel', topic: 'delivery', title: 'Track a parcel',
    keywords: ['track', 'tracking number', 'parcel', 'where is parcel', 'trace', 'follow parcel'],
    summary: 'Enter a tracking code to see the courier’s latest scan.',
    body: [
      'Every posted order has a tracking code. Enter it in the parcel tracker, or open the order to see the same timeline with delivery estimates.',
    ],
    link: { to: '/dashboard/orders', label: 'View my orders' },
  },
  {
    id: 'delivery-times', topic: 'delivery', title: 'Delivery times and delays',
    keywords: ['how long', 'delivery time', 'delay', 'slow', 'when arrive', 'estimated delivery', 'taking long'],
    summary: 'Most items arrive within the estimate shown on the order; couriers occasionally run late.',
    body: [
      'Delivery speed depends on the seller’s postage choice and the courier. The order shows an estimated delivery date and updates with each scan.',
      'Occasional courier delays happen. Your payment stays on hold the whole time, and if a parcel stalls well past its estimate you can get help from the order.',
    ],
  },
  {
    id: 'wrong-address', topic: 'delivery', title: 'I entered the wrong delivery address',
    keywords: ['wrong address', 'change address', 'update address', 'address mistake', 'redeliver'],
    summary: 'Act fast - if the item hasn’t shipped, contact the seller from the order.',
    body: [
      'If the seller hasn’t posted yet, message them from the order straight away to update the address, or ask to cancel and re-order with the correct details.',
      'Once a parcel is in transit the address usually can’t be changed, so please double-check your address at checkout.',
    ],
    link: { to: '/dashboard/orders', label: 'Go to my orders' },
  },

  /* ---- Account & sign-in ---- */
  {
    id: 'cant-sign-in', topic: 'account', title: 'I can’t sign in',
    keywords: ['login', 'sign in', 'cant log in', 'can’t log in', 'locked out', 'access account', 'wrong password'],
    summary: 'Reset your password, or sign in with Google if you used that.',
    body: [
      'If your password isn’t working, use "Forgotten your password?" to set a new one - the reset link is single-use and expires shortly for your security.',
      'If you originally joined with Google, use that same button to sign in rather than a password.',
    ],
    link: { to: '/forgot', label: 'Reset my password' },
  },
  {
    id: 'reset-password', topic: 'account', title: 'Reset your password',
    keywords: ['forgot password', 'reset password', 'change password', 'new password', 'password reset'],
    summary: 'We email you a secure link to set a new password.',
    body: [
      'Enter your email on the password reset page and we’ll send a link to choose a new password. The link works once and expires after a short window.',
      'Setting a new password signs out any existing sessions, so you’re the only one with access.',
    ],
    link: { to: '/forgot', label: 'Email me a reset link' },
  },
  {
    id: 'verify-email', topic: 'account', title: 'Verify your email address',
    keywords: ['verify email', 'confirmation code', 'email code', 'confirm email', 'didn’t get code', 'resend code'],
    summary: 'Enter the code we email you. Didn’t get it? Resend from the verify page.',
    body: [
      'After you sign up we email a short verification code. Enter it to confirm your address. The code expires quickly, so request it when you’re ready to type it in.',
      'No code? Check spam, then use Resend on the verify page for a fresh one.',
    ],
    link: { to: '/verify-email', label: 'Verify my email' },
  },
  {
    id: 'social-login', topic: 'account', title: 'Sign in with Google',
    keywords: ['google', 'social login', 'continue with google', 'sign in with'],
    summary: 'One-tap sign-in with your Google account.',
    body: [
      'You can join or sign in with Continue with Google. If you already have a Souqli account with the same email, it links automatically so you keep your orders and listings.',
    ],
    link: { to: '/login', label: 'Go to sign in' },
  },
  {
    id: 'close-account', topic: 'account', title: 'Close your account or delete your data',
    keywords: ['delete account', 'close account', 'remove account', 'delete data', 'gdpr', 'erase'],
    summary: 'Email support to close your account and exercise your data rights.',
    body: [
      'You can access, export or delete your personal data. To close your account or make a data request, email our support team and we’ll take it from there.',
      'If you have open orders or a wallet balance, we’ll help you settle those first.',
    ],
  },

  /* ---- ID verification ---- */
  {
    id: 'why-verify', topic: 'verification', title: 'Why do I need to verify my ID?',
    keywords: ['why verify', 'id', 'identity', 'verification', 'kyc', 'why id', 'not verified'],
    summary: 'Verification keeps the marketplace genuine and is required to sell and to pay out.',
    body: [
      'Verifying identity keeps Souqli free of fraud and stolen goods, and it’s required before you can receive payouts as a seller. Buying doesn’t require verification.',
      'If you see an "ID not verified" prompt on your dashboard, tap it to go straight to the identity section of your profile.',
    ],
    link: { to: '/dashboard/profile', label: 'Verify my identity' },
  },
  {
    id: 'how-verify', topic: 'verification', title: 'How to verify your identity',
    keywords: ['verify id', 'upload id', 'passport', 'driving licence', 'driving license', 'document', 'how to verify'],
    summary: 'Upload a government ID from your profile; your documents are encrypted.',
    body: [
      'Go to your profile’s identity section and follow the prompts to upload a government-issued ID. Your documents are encrypted and only used for verification.',
    ],
    steps: [
      'Open My Souqli → Profile.',
      'Find the Identity / verification section.',
      'Upload a clear photo of your ID and submit.',
    ],
    link: { to: '/dashboard/profile', label: 'Go to verification' },
  },
  {
    id: 'verify-time', topic: 'verification', title: 'How long does verification take?',
    keywords: ['how long verify', 'verification time', 'still pending', 'waiting verification', 'review id'],
    summary: 'Most checks complete quickly; you’ll be notified when it’s approved.',
    body: [
      'Most verifications are reviewed promptly. You can keep buying while it’s pending; selling payouts unlock once you’re approved.',
      'If it’s taking longer than expected, make sure your uploaded ID is clear and not expired, then contact support.',
    ],
  },

  /* ---- Trust & safety ---- */
  {
    id: 'buyer-protection', topic: 'trust', title: 'Souqli buyer protection',
    keywords: ['buyer protection', 'protected', 'guarantee', 'covered', 'safe', 'refund guarantee'],
    summary: 'Every purchase is held until you confirm receipt and covered if it doesn’t arrive or isn’t as described.',
    body: [
      'When you buy on Souqli, your payment is held until you confirm receipt and only released once you have the item as described. If it doesn’t arrive, or arrives damaged, fake or wrong, open a dispute within the window and we’ll put it right.',
      'Keeping payment and messages on Souqli is what makes this protection possible.',
    ],
    link: { to: '/trust', label: 'How it works' },
  },
  {
    id: 'open-dispute', topic: 'trust', title: 'Open or manage a dispute',
    keywords: ['dispute', 'complaint', 'claim', 'problem with order', 'raise issue', 'report order'],
    summary: 'Raise a dispute from the order and we’ll mediate while funds stays on hold.',
    body: [
      'If you and a seller can’t resolve an issue directly, open a dispute from the order. Add photos and details, and our team mediates. Funds remain on hold until it’s resolved.',
    ],
    steps: [
      'Open the affected order.',
      'Choose "Get help with this order" and pick the reason.',
      'Add evidence and submit - we’ll review and update you.',
    ],
    link: { to: '/dashboard/orders', label: 'Get help with an order' },
  },
  {
    id: 'stolen-counterfeit', topic: 'trust', title: 'Stolen or counterfeit goods',
    keywords: ['stolen', 'fake', 'counterfeit', 'imei', 'serial', 'genuine', 'replica', 'report seller'],
    summary: 'Prohibited on Souqli. We screen listings and check device serials/IMEIs.',
    body: [
      'Counterfeit and stolen items are banned. Listings are reviewed before going live, and device serials/IMEIs can be checked against stolen-goods databases.',
      'If you suspect an item is fake or stolen, don’t complete the purchase off-platform - report it from the listing or order and we’ll investigate.',
    ],
  },
  {
    id: 'stay-safe', topic: 'trust', title: 'Stay safe and avoid scams',
    keywords: ['scam', 'fraud', 'safe', 'avoid scam', 'off platform', 'phishing', 'suspicious'],
    summary: 'Keep payments and chat on Souqli - that’s what keeps you protected.',
    body: [
      'Never pay or be paid outside Souqli. If a seller or buyer asks you to move to bank transfer, email or another app, that’s a red flag - off-platform payments have no buyer protection and no protection.',
      'We’ll never ask for your password. Report anything suspicious from the listing, order, or by contacting support.',
    ],
  },

  /* ---- Membership ---- */
  {
    id: 'membership-benefits', topic: 'membership', title: 'What’s included in Souqli Member?',
    keywords: ['membership', 'member', 'subscription', 'benefits', 'waive fee', 'perks', 'premium', 'what included'],
    summary: 'An optional subscription that waives the buyer service fee and adds member perks.',
    body: [
      'Souqli Member is an optional paid subscription. Its headline benefit is a waived buyer service fee, alongside other member perks, for as long as your membership is active.',
      'It’s billed monthly or annually and you can cancel any time.',
    ],
    link: { to: '/membership', label: 'See Membership' },
  },
  {
    id: 'manage-membership', topic: 'membership', title: 'Manage or cancel your Membership',
    keywords: ['cancel membership', 'billing', 'stop subscription', 'unsubscribe', 'manage membership', 'renew'],
    summary: 'Cancel any time from your dashboard and keep benefits until the period ends.',
    body: [
      'You can manage or cancel your Membership from your dashboard. If you cancel, you keep member benefits until the end of the period you’ve already paid for, then it simply stops renewing.',
    ],
    link: { to: '/dashboard/membership', label: 'Manage Membership' },
  },
];

/* ─────────────────── Popular / anticipated questions ─────────────────── */
/** Shown before anyone searches - the things people most often need. */
export const POPULAR_IDS = [
  'track-order',
  'get-paid',
  'how-escrow',
  'item-not-as-described',
  'reset-password',
  'how-verify',
  'seller-fees',
  'how-returns-work',
  'buyer-protection',
  'membership-benefits',
];

/* ─────────────────────────── Smart matcher ─────────────────────────── */

const STOPWORDS = new Set([
  'the', 'a', 'an', 'and', 'or', 'to', 'of', 'is', 'it', 'my', 'i', 'do', 'how',
  'can', 'for', 'on', 'in', 'me', 'you', 'your', 'with', 'be', 'am', 'this',
  'that', 'get', 'help', 'need', 'want', 'please', 'souqli',
]);

function tokenize(s: string): string[] {
  return s
    .toLowerCase()
    .replace(/[’']/g, '’')
    .replace(/[^a-z0-9’ ]+/g, ' ')
    .split(/\s+/)
    .filter((t) => t.length > 1 && !STOPWORDS.has(t));
}

export interface HelpMatch {
  article: HelpArticle;
  score: number;
}

/**
 * Rank articles against a plain-language query. Deterministic and offline:
 *   +4  the whole query appears in a keyword phrase (or vice-versa)
 *   +3  a query token appears in the title
 *   +2  a query token matches a keyword token
 *   +1  a query token appears in the summary or body
 * Returns the best matches (score > 0), highest first.
 */
export function searchHelp(query: string, limit = 8): HelpMatch[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const tokens = tokenize(q);
  if (tokens.length === 0) return [];

  const matches: HelpMatch[] = [];

  for (const article of ARTICLES) {
    let score = 0;
    const titleL = article.title.toLowerCase();
    const summaryL = article.summary.toLowerCase();
    const bodyL = article.body.join(' ').toLowerCase();
    const keywordText = article.keywords.join(' ').toLowerCase();

    // Whole-phrase overlap with any keyword phrase.
    for (const kw of article.keywords) {
      const k = kw.toLowerCase();
      if (k.length >= 4 && (q.includes(k) || (k.includes(q) && q.length >= 4))) {
        score += 4;
        break;
      }
    }

    for (const t of tokens) {
      if (titleL.includes(t)) score += 3;
      // keyword token match
      const kwTokens = keywordText.split(/\s+/);
      if (kwTokens.includes(t)) score += 2;
      else if (keywordText.includes(t)) score += 1;
      if (summaryL.includes(t)) score += 1;
      else if (bodyL.includes(t)) score += 1;
    }

    if (score > 0) matches.push({ article, score });
  }

  matches.sort((a, b) => b.score - a.score);
  return matches.slice(0, limit);
}

/* ─────────────────────────── Lookups ─────────────────────────── */

export function articleById(id: string): HelpArticle | undefined {
  return ARTICLES.find((a) => a.id === id);
}

export function articlesByTopic(topicId: string): HelpArticle[] {
  return ARTICLES.filter((a) => a.topic === topicId);
}

export function topicById(id: string): HelpTopic | undefined {
  return TOPICS.find((t) => t.id === id);
}

export const POPULAR_ARTICLES: HelpArticle[] = POPULAR_IDS
  .map(articleById)
  .filter((a): a is HelpArticle => !!a);
